import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { CartProvider, useCart } from "@/context/CartContext";
import { ProTopBar } from "@/components/ProHighlight";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import ProductPage from "@/pages/ProductPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import LegalPage from "@/pages/LegalPage";
import ContactPage from "@/pages/ContactPage";
import OrderTracking from "@/pages/OrderTracking";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import Ambassador from "@/pages/Ambassador";
import AdminReviews from "@/pages/AdminReviews";
import AdminOrders from "@/pages/AdminOrders";
import InstagramPage from "@/pages/InstagramPage";
import AdminInstagram from "@/pages/AdminInstagram";
import ResourcePage from "@/pages/ResourcePage";
import AboutPage from "@/pages/AboutPage";
import { HairGuidesHub, HairGuidePage } from "@/pages/HairGuides";
import ProLayout, { RequireApproved, RequireUser } from "@/pages/pro/ProLayout";
import ProCatalog from "@/pages/pro/ProCatalog";
import ProProduct from "@/pages/pro/ProProduct";
import ProCart from "@/pages/pro/ProCart";
import ProCheckout, { ProSuccess } from "@/pages/pro/ProCheckout";
import ProAccount from "@/pages/pro/ProAccount";
import { ProLogin, ProRegister, ProForgot, ProReset } from "@/pages/pro/ProAuth";
import AdminPro from "@/pages/AdminPro";
import AdminProCodes from "@/pages/AdminProCodes";
import AdminOrderForm from "@/pages/AdminOrderForm";
import AdminQuoteSim from "@/pages/AdminQuoteSim";
import AdminStats from "@/pages/AdminStats";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import { VisitTracker } from "@/components/VisitTracker";
import GeoPage from "@/pages/GeoPage";
import { GEO_PAGES, GEO_HUB_PAGE } from "@/data/geoPages";
import ProHome from "@/pages/ProHome";
import UniversePage from "@/pages/UniversePage";
import ProAdvantages from "@/pages/ProAdvantages";
import { UNIVERSES } from "@/data/universes";
import { SEO_PAGES } from "@/data/seoProPages";
import AdminProspects from "@/pages/AdminProspects";
import ResellerPage from "@/pages/ResellerPage";
import ResellerPageEn from "@/pages/ResellerPageEn";
import AdminHub from "@/pages/AdminHub";
import { RESOURCE_LIST } from "@/data/seoPages";
import { setRef } from "@/lib/ref";

const RefCapture = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) setRef(ref);
  }, [location.search]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const MobileCta = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setOpen, count } = useCart();
  if (location.pathname !== "/packs") return null;

  const go = () => {
    document.getElementById("packs")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 p-3 bg-white/90 backdrop-blur-md border-t border-[var(--oz-border)] flex gap-2">
      <button
        data-testid="mobile-sticky-packs"
        onClick={go}
        className="flex-1 inline-flex items-center justify-center px-5 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm tracking-wide"
      >
        CHOISIR MON PACK
      </button>
      {count > 0 && (
        <button
          data-testid="mobile-sticky-cart"
          onClick={() => setOpen(true)}
          className="px-5 py-3.5 rounded-full bg-[var(--oz-gold)] text-white font-semibold text-sm"
        >
          Panier ({count})
        </button>
      )}
    </div>
  );
};

const Layout = ({ children }) => (
  <>
    <ProTopBar />
    <Header />
    <main className="min-h-[50vh] pb-20 lg:pb-0">{children}</main>
    <Footer />
    <CartDrawer />
    <MobileCta />
    <Toaster />
  </>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CartProvider>
          <ScrollToTop />
          <RefCapture />
          <Layout>
            <VisitTracker />
            <Routes>
              <Route path="/" element={<ProHome />} />
              <Route path="/packs" element={<HomePage />} />
              {[...UNIVERSES, ...SEO_PAGES].map((u) => <Route key={u.key} path={u.path} element={<UniversePage u={u} />} />)}
              <Route path="/avantages-professionnels" element={<ProAdvantages />} />
              <Route path="/pack/:slug" element={<ProductPage />} />
              <Route path="/produit/:key" element={<ProductDetailPage />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/suivi" element={<OrderTracking />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/ambassadeur" element={<Ambassador />} />
              <Route path="/instagram" element={<InstagramPage />} />
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/devenir-revendeur" element={<ResellerPage />} />
              <Route path="/en/become-a-reseller" element={<ResellerPageEn />} />
              <Route path="/conseils-cheveux-afro" element={<HairGuidesHub />} />
              <Route path="/conseils-cheveux-afro/:slug" element={<HairGuidePage />} />
              {RESOURCE_LIST.map((r) => (
                <Route key={r.slug} path={r.path} element={<ResourcePage slug={r.slug} />} />
              ))}
              <Route path={GEO_HUB_PAGE.path} element={<GeoPage g={GEO_HUB_PAGE} />} />
              {GEO_PAGES.map((g) => <Route key={g.key} path={g.path} element={<GeoPage g={g} />} />)}
              <Route path="/admin/avis" element={<AdminReviews />} />
              <Route path="/admin" element={<AdminHub />} />
              <Route path="/admin/espace-pro" element={<AdminPro />} />
              <Route path="/admin/prospection" element={<AdminProspects />} />
              <Route path="/admin/codes-pro" element={<AdminProCodes />} />
              <Route path="/admin/bon-de-commande" element={<AdminOrderForm />} />
              <Route path="/admin/simulation-devis" element={<AdminQuoteSim />} />
              <Route path="/admin/statistiques" element={<AdminStats />} />
              <Route path="/admin/codes-promo" element={<AdminPromoCodes />} />
              <Route path="/espace-pro" element={<ProLayout />}>
                <Route index element={<ProCatalog />} />
                <Route path="produit/:slug" element={<ProProduct />} />
                <Route path="panier" element={<RequireApproved><ProCart /></RequireApproved>} />
                <Route path="commande" element={<RequireApproved><ProCheckout /></RequireApproved>} />
                <Route path="commande/succes" element={<RequireUser><ProSuccess /></RequireUser>} />
                <Route path="compte" element={<RequireUser><ProAccount /></RequireUser>} />
                <Route path="connexion" element={<ProLogin />} />
                <Route path="inscription" element={<ProRegister />} />
                <Route path="mot-de-passe-oublie" element={<ProForgot />} />
                <Route path="reinitialiser" element={<ProReset />} />
              </Route>
              <Route path="/admin/commandes" element={<AdminOrders />} />
              <Route path="/admin/instagram" element={<AdminInstagram />} />
            </Routes>
          </Layout>
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
